import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  TableCell,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid2 as Grid,
  useMediaQuery,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  LocalHospital as ClinicIcon,
  CloudUpload as CloudUploadIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { usePages } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';

import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PageTypeSelector from '../components/pages/PageTypeSelector';
import PagesEmptyState from '../components/pages/PagesEmptyState';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import AppSnackbar from '../components/ui/AppSnackbar';
import { PageType } from '../catalogs/pageCatalog.generated';

// URL base das páginas públicas - usa variável de ambiente
const PAGES_BASE_URL = import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br';

// Helper: calcular status da página segundo §8.2
const getPageStatus = (page) => {
  if (!page) return { status: 'desconhecido', color: 'default', label: 'Desconhecido' };

  // Publicada: is_published = true
  if (page.isPublished) return { status: 'published', color: 'success', label: 'Publicada' };

  // Com alterações pendentes: draft_updated_at > published_at (implica que já foi publicada antes)
  if (page.draftUpdatedAt && page.publishedAt && new Date(page.draftUpdatedAt) > new Date(page.publishedAt)) {
    return { status: 'pending', color: 'warning', label: 'Alterações pendentes' };
  }

  // Despublicada: is_published = false && published_config != null (implica that it was published before)
  if (!page.isPublished && page.publishedAt && !page.hasPendingChanges) {
    return { status: 'unpublished', color: 'error', label: 'Despublicada' };
  }

  // Rascunho: draft_config != null && published_config == null (nunca foi publicada)
  // Ou: !isPublished && !publishedAt
  return { status: 'draft', color: 'default', label: 'Rascunho' };
};

// Chaveado pelo int do catálogo PageType (modelo v3): 1=LinkInBio, 2=Professional, 3=Company.
const PAGE_TYPE_CONFIG = {
  [PageType.LinkInBio]:   { label: 'Link in Bio', icon: <LinkIcon sx={{ fontSize: 14 }} />, color: 'primary' },
  [PageType.Professional]: { label: 'Profissional', icon: <PersonIcon sx={{ fontSize: 14 }} />, color: 'secondary' },
  [PageType.Company]:      { label: 'Empresa', icon: <ClinicIcon sx={{ fontSize: 14 }} />, color: 'info' },
};

function PageTypeChip({ type, variant = 'outlined', size = 'small' }) {
  const config = PAGE_TYPE_CONFIG[type] || { label: type, icon: null, color: 'default' };
  return (
    <Chip
      label={config.label}
      icon={config.icon}
      color={config.color}
      variant={variant}
      size={size}
    />
  );
}
import { useContextualHint } from '../hooks/useContextualHint';
import PageLayout from '../components/ui/PageLayout';
import StandardTable from '../components/ui/StandardTable';

export default function PagesPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hint = useContextualHint('pages', {});

  // React Query Hooks
  const { pages, loading, error, reload, publishPageAsync, unpublishPageAsync } = usePages();

  // Dialog States
  const typeSelector = useDialog();
  const deleteDialog = useDialog();
  const previewDialog = useDialog();
  const publishConfirmDialog = useDialog();
  const unpublishConfirmDialog = useDialog();

  // Snackbar State
  const { snackbar, hideSnackbar, showSuccess, showError } = useSnackbar();

  // Handlers
  const handleCreateNew = () => {
    typeSelector.handleOpen();
  };

  const handlePreview = (page) => {
    if (page.isPublished) {
      // Página publicada: mostra no dialog via iframe da URL pública
      previewDialog.handleOpen(page);
    } else {
      // Rascunho não publicado: abre editor (que tem preview completo)
      navigate(`/pages/edit/${page.id}`);
    }
  };

  const handlePublish = (page) => {
    publishConfirmDialog.handleOpen(page);
  };

  const handleUnpublish = (page) => {
    unpublishConfirmDialog.handleOpen(page);
  };

  const doPublish = async () => {
    try {
      await publishPageAsync(publishConfirmDialog.data.id);
      showSuccess('Página publicada com sucesso!');
    } catch (error) {
      showError(`Erro ao publicar: ${error.message || 'tente novamente'}`);
    } finally {
      publishConfirmDialog.handleClose();
    }
  };

  const doUnpublish = async () => {
    try {
      await unpublishPageAsync(unpublishConfirmDialog.data.id);
      showSuccess('Página despublicada com sucesso!');
    } catch (error) {
      showError(`Erro ao despublicar: ${error.message || 'tente novamente'}`);
    } finally {
      unpublishConfirmDialog.handleClose();
    }
  };

  const handleCopyUrl = (page) => {
    const url = `${PAGES_BASE_URL}/${page.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      showSuccess('URL copiada para a área de transferência!');
    }).catch(() => {
      showError('Erro ao copiar URL');
    });
  };

  // Loading/Error States
  if (loading) return <LoadingState message="Carregando páginas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const tableColumns = [
    { id: 'title', label: 'Título' },
    { id: 'url', label: 'URL Pública' },
    { id: 'type', label: 'Tipo' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Ações', align: 'right' },
  ];

  return (
    <PageLayout
      themeKey="pages"
      actionLabel="Nova Página"
      onAction={handleCreateNew}
      hint={hint}
    >
      {/* Empty State */}
      {pages.length === 0 ? (
        <PagesEmptyState />
      ) : isMobile ? (
        // MOBILE: Cards Grid
        <Grid container spacing={2}>
          {pages.map((page) => (
            <Grid size={{ xs: 12 }} key={page.id}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {page.title}
                      </Typography>
                      <Box display="flex" gap={1}>
                        {(() => {
                          const { color, label } = getPageStatus(page);
                          return (
                            <Chip label={label} size="small" color={color} variant="outlined" />
                          );
                        })()}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        component="a"
                        href={`${PAGES_BASE_URL}/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {PAGES_BASE_URL}/{page.slug}
                      </Box>
                      <Tooltip title="Copiar URL">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUrl(page)}
                          sx={{ p: 0 }}
                          aria-label="Copiar URL"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <PageTypeChip type={page.type} variant="outlined" />
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(page)}
                      color="primary"
                      disabled={false}
                      aria-label="Preview"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={!page.isPublished || page.hasPendingChanges ? 'Publicar' : 'Não há alterações'}>
                    <IconButton
                      size="small"
                      onClick={() => handlePublish(page)}
                      color="success"
                      disabled={page.isPublished && !page.hasPendingChanges}
                      aria-label="Publicar Página"
                    >
                      <CloudUploadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={page.isPublished ? 'Despublicar' : 'Não publicada'}>
                    <IconButton
                      size="small"
                      onClick={() => handleUnpublish(page)}
                      color="error"
                      disabled={!page.isPublished}
                      aria-label="Despublicar Página"
                    >
                      <CloudOffIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/pages/edit/${page.id}`)}
                      color="primary"
                      aria-label="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => deleteDialog.handleOpen(page)}
                      color="error"
                      aria-label="Excluir"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // DESKTOP: Table
        <StandardTable columns={tableColumns}>
          {pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {page.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="a"
                        href={`${PAGES_BASE_URL}/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {PAGES_BASE_URL}/{page.slug}
                      </Box>
                      <Tooltip title="Copiar URL completa">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUrl(page)}
                          sx={{ p: 0.5 }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <PageTypeChip type={page.type} variant="filled" />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const { color, label } = getPageStatus(page);
                      return (
                        <Chip
                          label={label}
                          size="small"
                          color={color}
                          variant="outlined"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(page)}
                        color="primary"
                        disabled={false}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={!page.isPublished || page.hasPendingChanges ? 'Publicar' : 'Não há alterações para publicar'}>
                      <IconButton
                        size="small"
                        onClick={() => handlePublish(page)}
                        color="success"
                        disabled={page.isPublished && !page.hasPendingChanges}
                      >
                        <CloudUploadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={page.isPublished ? 'Despublicar' : 'Página não está publicada'}>
                      <IconButton
                        size="small"
                        onClick={() => handleUnpublish(page)}
                        color="error"
                        disabled={!page.isPublished}
                      >
                        <CloudOffIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/pages/edit/${page.id}`)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => deleteDialog.handleOpen(page)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
          ))}
        </StandardTable>
      )}

      {/* Botão "Nova Página" Rodapé (TODO 3.5) */}
      {pages.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, py: 3 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            size="large"
          >
            Nova Página
          </Button>
        </Box>
      )}

      {/* Page Type Selector Modal */}
      <PageTypeSelector
        open={typeSelector.open}
        onClose={typeSelector.handleClose}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/pages/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.title}
      />

      {/* Preview Dialog com iframe (TODO 3.7) */}
      <Dialog
        open={previewDialog.open}
        onClose={previewDialog.handleClose}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: 'min(90vw, 1200px)',
            height: '80vh',
            maxHeight: 'none',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Preview: {previewDialog.data?.title}
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {previewDialog.data && (
            <Box
              component="iframe"
              src={`${PAGES_BASE_URL}/${previewDialog.data.slug}`}
              title={`Preview: ${previewDialog.data.title}`}
              sx={{ flex: 1, width: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={previewDialog.handleClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog (TODO 3.8) */}
      <ConfirmDialog
        open={publishConfirmDialog.open}
        onClose={publishConfirmDialog.handleClose}
        onConfirm={doPublish}
        title="Publicar Página"
        message={publishConfirmDialog.data?.hasPendingChanges
          ? 'Deseja publicar esta página? Alterações pendentes serão salvas.'
          : 'Deseja publicar esta página?'
        }
        confirmText="Publicar"
        confirmColor="success"
      />

      {/* Unpublish Confirmation Dialog (TODO 3.8) */}
      <ConfirmDialog
        open={unpublishConfirmDialog.open}
        onClose={unpublishConfirmDialog.handleClose}
        onConfirm={doUnpublish}
        title="Despublicar Página"
        message="Deseja despublicar esta página? Ela deixará de estar visível publicamente."
        confirmText="Despublicar"
        confirmColor="error"
      />

      {/* Snackbar */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
